import type { PublicationMedia } from "./types";

export function renderMarkdown(
  markdown: string,
  media: PublicationMedia[] = [],
): string {
  const mediaUrls = new Map(
    media.map((item) => [item.id.toLowerCase(), item.url]),
  );
  const lines = String(markdown || "")
    .replace(/\r\n?/g, "\n")
    .split("\n");
  const output: string[] = [];
  let list: "ul" | "ol" | null = null;
  let codeLanguage: string | null = null;
  const closeList = () => {
    if (list) output.push(`</${list}>`);
    list = null;
  };

  for (const rawLine of lines) {
    if (codeLanguage !== null && rawLine.trim().startsWith("```")) {
      closeList();
      output.push("</code></pre></div>");
      codeLanguage = null;
      continue;
    }
    if (codeLanguage !== null) {
      output.push(`${escapeHtml(rawLine)}\n`);
      continue;
    }
    const fence = rawLine.trim().match(/^```\s*([^\s`]*)/);
    if (fence) {
      closeList();
      codeLanguage = fence[1] || "plaintext";
      output.push(
        `<div class="markdown-code-block"><div class="markdown-code-header"><span>${escapeHtml(codeLanguage)}</span></div><pre><code>`,
      );
      continue;
    }
    const heading = rawLine.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      closeList();
      const level = heading[1].length;
      output.push(`<h${level}>${inline(heading[2], mediaUrls)}</h${level}>`);
      continue;
    }
    const unordered = rawLine.match(/^\s*[-*]\s+(.+)$/);
    const ordered = rawLine.match(/^\s*\d+\.\s+(.+)$/);
    if (unordered || ordered) {
      const nextList = unordered ? "ul" : "ol";
      if (list !== nextList) {
        closeList();
        list = nextList;
        output.push(`<${list}>`);
      }
      output.push(
        `<li>${inline((unordered || ordered)?.[1] || "", mediaUrls)}</li>`,
      );
      continue;
    }
    closeList();
    const quote = rawLine.match(/^>\s?(.+)$/);
    if (quote) {
      output.push(`<blockquote>${inline(quote[1], mediaUrls)}</blockquote>`);
    } else if (rawLine.trim()) {
      output.push(`<p>${inline(rawLine, mediaUrls)}</p>`);
    }
  }
  closeList();
  if (codeLanguage !== null) output.push("</code></pre></div>");
  return output.join("");
}

export function wrapMarkdownCodeBlock(
  source: string,
  selectionStart: number,
  selectionEnd: number,
): { value: string; selectionStart: number; selectionEnd: number } {
  const start = Math.max(0, Math.min(selectionStart, source.length));
  const end = Math.max(start, Math.min(selectionEnd, source.length));
  const selected = source.slice(start, end);
  const code = selected || "代码";
  const leadingBreak = start > 0 && source[start - 1] !== "\n" ? "\n" : "";
  const trailingBreak = end < source.length && source[end] !== "\n" ? "\n" : "";
  const opening = `${leadingBreak}\`\`\`\n`;
  const closing = `${code.endsWith("\n") ? "" : "\n"}\`\`\`${trailingBreak}`;

  return {
    value: `${source.slice(0, start)}${opening}${code}${closing}${source.slice(end)}`,
    selectionStart: start + opening.length,
    selectionEnd: start + opening.length + code.length,
  };
}

function inline(value: string, mediaUrls: Map<string, string>): string {
  let result = escapeHtml(value);
  result = result.replace(
    /!\[([^\]]*)\]\(media:\/\/([0-9a-f-]{36})\)/gi,
    (_match, alt: string, id: string) => {
      const url = mediaUrls.get(id.toLowerCase());
      return url
        ? `<img src="${escapeAttribute(url)}" alt="${escapeAttribute(alt)}" loading="lazy">`
        : `<span class="markdown-image-pending">图片正在等待发布</span>`;
    },
  );
  result = result.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi,
    '<a href="$2" target="_blank" rel="noreferrer">$1</a>',
  );
  result = result.replace(/`([^`]+)`/g, "<code>$1</code>");
  result = result.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  result = result.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  result = result.replace(/(^|[^*])\*([^*]+)\*/g, "$1$2");
  result = result.replace(/(^|[^\w])_([^_]+)_(?!\w)/g, "$1$2");
  return result;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll("`", "&#96;");
}
